<script setup lang="ts">
/* =============================================================
   AMENITY PICKER
   =============================================================
   The five amenities as checkboxes, laid out like one field so a
   form row does not break rhythm with TextField and SelectField
   beside it.

   Checkboxes rather than a multi-select: there are five, they
   all fit, and a native multi-select is the worst control on the
   web — you cannot see what is chosen without scrolling it, and
   ctrl-clicking to deselect is folklore.

   The options come from useAmenities(), which is a compiled-in
   map rather than a fetch — so there is no loading or failure
   state here, and the list cannot arrive late or empty. The
   database validates the codes through the `amenity_code` enum;
   these are only the words. See amenities.ts.
   ============================================================= */

import { computed, useId } from 'vue'
import { useAmenities } from '@/lib/amenities'
import type { AmenityCode } from '@/lib/types'

const props = withDefaults(
  defineProps<{ modelValue?: AmenityCode[]; label?: string; hint?: string; error?: string }>(),
  { modelValue: () => [] },
)

const emit = defineEmits<{ 'update:modelValue': [value: AmenityCode[]] }>()

const id = useId()
const { amenities } = useAmenities()

const chosen = computed(() => new Set(props.modelValue))

/* Emits a fresh array in the list's own order rather than mutating, so the
   parent's dirty check sees a new value and the order does not depend on the
   sequence somebody clicked in. */
function toggle(code: AmenityCode): void {
  const next = amenities
    .map((a) => a.code)
    .filter((c) => (c === code ? !chosen.value.has(c) : chosen.value.has(c)))
  emit('update:modelValue', next)
}
</script>

<template>
  <fieldset class="field">
    <legend class="label">{{ label ?? 'Amenities' }}</legend>

    <div class="options">
      <label v-for="a in amenities" :key="a.code" class="option">
        <input
          type="checkbox"
          class="box"
          :name="`${id}-${a.code}`"
          :checked="chosen.has(a.code)"
          @change="toggle(a.code)"
        />
        <span>{{ a.label }}</span>
      </label>
    </div>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-else-if="hint" class="hint">{{ hint }}</p>
  </fieldset>
</template>

<style scoped>
/* A fieldset carries the grouping for assistive tech, but brings a border,
   padding and margin that no other field has. Stripped back to TextField's
   box so the two line up. */
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
  min-inline-size: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.label {
  padding: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-heading);
}

.options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3xs) var(--space-sm);
  align-items: center;
  min-height: var(--control-height);
}

.option {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  font-size: var(--text-sm);
  color: var(--color-text);
}

.box {
  inline-size: 1rem;
  block-size: 1rem;
  accent-color: var(--color-accent);
}

.box:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
  border-radius: 2px;
}

.hint,
.error {
  font-size: var(--text-xs);
  line-height: var(--leading-snug);
}

.hint {
  color: var(--color-text-faint);
}

.error {
  color: var(--color-danger);
}
</style>
